import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown, Users, CheckCircle, Send, Split, FlagTriangleRight, Cpu, BarChart3, Info } from 'lucide-react';
// Logo is now referenced from public folder

// DEPLOYMENT CONFIGURATION
const API_PROXY_URL = 'https://upskillingcircle.com/api/chat.php';
const NOTION_API_URL = 'https://upskillingcircle.com/api/notion.php';
const STORAGE_API_URL = 'https://upskillingcircle.com/api/store_assessment.php';
const EMAIL_API_URL = 'https://upskillingcircle.com/api/send_results_email.php';

// CONFIDENCE LEVEL DOCUMENTATION
// HIGH: User provided clear numeric choice after seeing multiple choice options
// MEDIUM: LLM successfully scored response with reasonable certainty (default level)  
// LOW: Fallback scoring used, error conditions, or LLM uncertainty

const AIReadinessAssessment = () => {
  // Add CSS animation styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeOut {
        from { opacity: 1; transform: translateY(0); }
        to { opacity: 0; transform: translateY(-10px); }
      }
      @keyframes slideInFromBottom {
        from { opacity: 0; transform: translateY(40px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideOutUp {
        from { opacity: 1; transform: translateY(0); }
        to { opacity: 0; transform: translateY(-40px); }
      }
      @keyframes assessmentStartFadeIn {
        from { opacity: 0; transform: translateY(30px) scale(0.95); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      .fade-in-animation {
        animation: fadeIn 0.6s ease-out forwards;
      }
      .fade-out-animation {
        animation: fadeOut 0.4s ease-in forwards;
      }
      .slide-in-from-bottom {
        animation: slideInFromBottom 0.5s ease-out forwards;
      }
      .slide-out-up {
        animation: slideOutUp 0.4s ease-in forwards;
      }
      .assessment-start-animation {
        animation: assessmentStartFadeIn 0.8s ease-out forwards;
      }
      .faq-answer {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.4s ease-out, opacity 0.3s ease-out, padding 0.3s ease-out;
        opacity: 0;
        padding-top: 0;
      }
      .faq-answer.expanded {
        max-height: 300px;
        opacity: 1;
        padding-top: 1rem;
      }
      .faq-chevron {
        transition: transform 0.3s ease-out, color 0.2s ease-out;
      }
      .faq-chevron.rotated {
        transform: rotate(180deg);
      }
      .faq-card {
        min-height: 120px;
      }
      @media (min-width: 768px) {
        .faq-card .faq-answer.expanded {
          max-height: 200px;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [currentStage, setCurrentStage] = useState('welcome');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [scores, setScores] = useState({});
  const [confidenceLevels, setConfidenceLevels] = useState({});
  const [currentResponse, setCurrentResponse] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [waitingForClarification, setWaitingForClarification] = useState(false);
  
  // Timing states
  const [assessmentStartTime, setAssessmentStartTime] = useState(null);
  const [questionStartTimes, setQuestionStartTimes] = useState({});
  const [questionDurations, setQuestionDurations] = useState({});
  
  // Skip/Report states
  const [skippedQuestions, setSkippedQuestions] = useState(new Set());
  const [reportedIssues, setReportedIssues] = useState({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportDescription, setReportDescription] = useState('');
  const [showEndAssessmentModal, setShowEndAssessmentModal] = useState(false);

  // Connection retry states
  const [connectionState, setConnectionState] = useState('connected'); // 'connected', 'retrying', 'failed', 'user_choice'
  const [showConnectionOptions, setShowConnectionOptions] = useState(false);
  const [scoringMethods, setScoringMethods] = useState({}); // tracks 'ai' or 'self' per question
  const [currentRetryAttempt, setCurrentRetryAttempt] = useState(0);
  const [retryCountdown, setRetryCountdown] = useState(0);

  // Use ref to track if user made a choice (to stop background retries)
  const userMadeChoiceRef = useRef(false);

  // Ref for conversation history container to enable auto-scroll
  const conversationRef = useRef(null);

  // Name and Welcome states
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [userPhone, setUserPhone] = useState('');
  const [userOrganization, setUserOrganization] = useState('');
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [isRestarting, setIsRestarting] = useState(false);

  // Track which questions have had "Provide Choices" used
  const [usedProvideChoices, setUsedProvideChoices] = useState(new Set());

  // Qualification form states
  const [userPosition, setUserPosition] = useState('');
  const [userPositionOther, setUserPositionOther] = useState('');
  const [oversightAreas, setOversightAreas] = useState([]);
  const [isQualified, setIsQualified] = useState(null);
  const [qualificationQuestionIndex, setQualificationQuestionIndex] = useState(0);
  const [showEmailTemplate, setShowEmailTemplate] = useState(false);
  const [showWaitingListConfirmation, setShowWaitingListConfirmation] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [questionAnimating, setQuestionAnimating] = useState(false);
  const [modalClosing, setModalClosing] = useState(false);
  const [assessmentStarting, setAssessmentStarting] = useState(false);

  // FAQ toggle states
  const [expandedFAQs, setExpandedFAQs] = useState({});

  // FAQ data structure
  const faqData = [
    {
      id: 'time',
      question: 'How long does it take?',
      answer: '10-15 minutes. Take your time for the most accurate results.'
    },
    {
      id: 'data',
      question: 'What happens to my data?',
      answer: 'Completely confidential. Used only for your report, never shared with third parties.'
    },
    {
      id: 'knowledge',
      question: 'Do I need technical knowledge?',
      answer: 'No technical knowledge needed. Focuses on organizational readiness and strategy, not technical details.'
    },
    {
      id: 'receive',
      question: 'What will I receive?',
      answer: 'Comprehensive report with readiness score, analysis, recommendations, and implementation roadmap.'
    }
  ];

  // Toggle FAQ expansion
  const toggleFAQ = (faqId) => {
    setExpandedFAQs(prev => ({
      ...prev,
      [faqId]: !prev[faqId]
    }));
  };

  // Add page unload handler to capture abandoned assessments
  useEffect(() => {
    const handleBeforeUnload = async (e) => {
      // Only send if user has started assessment and hasn't completed it
      if (currentStage === 'assessment' && submissionId && Object.keys(responses).length > 0) {
        // Build the same format as sendToNotion function
        const beaconData = {
          submission_id: submissionId,  // Note: submission_id not submissionId
          name: userName.trim(),
          email: userEmail.trim(),
          phone: userPhone.trim(),
          organization: userOrganization.trim(),
          timestamp: new Date().toISOString(),
          status: 'abandoned',
          completion_percentage: Math.round((Object.keys(responses).length / questions.length) * 100),
          total_time_minutes: Math.round((getTotalAssessmentTime() / 1000) / 60),
          total_time_seconds: getTotalAssessmentTime() / 1000,
          overall_score: 0, // Will be calculated later if they complete
          readiness_level: 'In Progress',
          answered_questions: Object.keys(responses).length,
          total_questions: questions.length,
          skipped_questions: skippedQuestions.size,
          reported_issues: Object.keys(reportedIssues).length,
          average_question_time: 0, // Will be calculated later
          confidence_data: null,
          low_confidence_scores: null,
          // Add qualification data
          qualification: {
            userPosition: userPosition,
            userPositionOther: userPositionOther,
            oversightAreas: oversightAreas,
            isQualified: isQualified
          },
          // Add partial conversation history for storage
          conversationHistory: conversationHistory
        };

        try {
          // Use navigator.sendBeacon for reliable data sending during page unload
          navigator.sendBeacon(
            NOTION_API_URL,
            JSON.stringify(beaconData)
          );
          
          // Create storage-specific format (same data but structured for storage API)
          const storageData = {
            submission_id: submissionId,
            timestamp: beaconData.timestamp,
            status: 'abandoned',
            name: userName.trim(),
            email: userEmail.trim(),
            phone: userPhone.trim(),
            organization: userOrganization.trim(),
            qualification: beaconData.qualification,
            conversationHistory: conversationHistory,
            // Include assessment progress data
            completionStatus: {
              answeredQuestions: Object.keys(responses).length,
              totalQuestions: questions.length,
              completionPercentage: beaconData.completion_percentage,
              isComplete: false
            },
            timing: {
              totalTimeSeconds: beaconData.total_time_seconds,
              totalTimeMinutes: beaconData.total_time_minutes
            }
          };
          
          // Store complete data (including conversations)
          navigator.sendBeacon(
            STORAGE_API_URL,
            JSON.stringify(storageData)
          );
        } catch (error) {
          console.error('Failed to send abandoned assessment data:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStage, submissionId, responses, skippedQuestions, reportedIssues, userName, userEmail, userPhone, userOrganization]);

  // Add Esc key to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && showUserInfoModal) {
        setShowUserInfoModal(false);
      }
    };

    if (showUserInfoModal) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [showUserInfoModal]);

  // Scroll to top when qualification stage loads or question changes
  useEffect(() => {
    if (currentStage === 'qualification' || currentStage === 'disqualified') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStage, qualificationQuestionIndex]);

  // Scroll to top when assessment stage starts
  useEffect(() => {
    if (currentStage === 'assessment') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStage]);

  // Auto-scroll conversation history when new messages are added
  useEffect(() => {
    if (conversationHistory[currentQuestionIndex] && conversationHistory[currentQuestionIndex].length > 0) {
      // Small delay to ensure DOM updates before scrolling
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [conversationHistory, currentQuestionIndex]);

  // Timing functions
  const startAssessmentTimer = () => {
    const startTime = Date.now();
    setAssessmentStartTime(startTime);
    setQuestionStartTimes({ 0: startTime });
  };

  const startQuestionTimer = (questionIndex) => {
    const startTime = Date.now();
    setQuestionStartTimes(prev => ({
      ...prev,
      [questionIndex]: startTime
    }));
  };

  const endQuestionTimer = (questionIndex) => {
    const endTime = Date.now();
    const startTime = questionStartTimes[questionIndex];
    if (startTime) {
      const duration = endTime - startTime;
      setQuestionDurations(prev => ({
        ...prev,
        [questionIndex]: duration
      }));
    }
  };

  const getTotalAssessmentTime = () => {
    if (!assessmentStartTime) return 0;
    return Date.now() - assessmentStartTime;
  };

  const getTimingReport = () => {
    const totalTime = getTotalAssessmentTime();
    const questionTimes = Object.entries(questionDurations).map(([index, duration]) => ({
      question: parseInt(index) + 1,
      duration: Math.round(duration / 1000), // Convert to seconds
      durationText: formatDuration(duration)
    }));

    return {
      totalTimeMs: totalTime,
      totalTimeSeconds: Math.round(totalTime / 1000),
      totalTimeText: formatDuration(totalTime),
      questionTimes,
      averageQuestionTime: questionTimes.length > 0 
        ? Math.round(questionTimes.reduce((sum, q) => sum + q.duration, 0) / questionTimes.length)
        : 0
    };
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };


  // Auto-scroll conversation history to bottom
  const scrollToBottom = () => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  };

  // Skip/Report functions
  const skipQuestion = (reason = 'user_skip') => {
    const currentIndex = currentQuestionIndex;
    
    // End timer for current question
    endQuestionTimer(currentIndex);
    
    // Mark as skipped
    setSkippedQuestions(prev => new Set([...prev, currentIndex]));
    
    // Set a default response for skipped questions
    setResponses(prev => ({
      ...prev,
      [currentIndex]: `SKIPPED_${reason}`
    }));
    
    // Set neutral score for skipped questions
    setScores(prev => ({
      ...prev,
      [currentIndex]: 3 // Neutral score
    }));
    
    // Add to conversation history
    const skipMessage = {
      role: 'system',
      content: `Question skipped by user (${reason})`
    };
    
    setConversationHistory(prev => ({
      ...prev,
      [currentIndex]: [...(prev[currentIndex] || []), skipMessage]
    }));
    
    // Move to next question or results
    if (currentIndex < questions.length - 1) {
      const nextQuestionIndex = currentIndex + 1;
      setCurrentQuestionIndex(nextQuestionIndex);
      startQuestionTimer(nextQuestionIndex);
    } else {
      setCurrentStage('results');
    }
  };

  const openReportModal = () => {
    setShowReportModal(true);
    setReportDescription('');
  };

  const submitReport = () => {
    const currentIndex = currentQuestionIndex;
    
    setReportedIssues(prev => ({
      ...prev,
      [currentIndex]: {
        type: 'user_reported',
        description: reportDescription || 'No description provided',
        timestamp: new Date().toISOString(),
        conversationContext: conversationHistory[currentIndex] || []
      }
    }));
    
    setShowReportModal(false);
    setReportDescription('');
    
    // Show confirmation before skipping
    setTimeout(() => {
      alert('Issue reported successfully. Thank you for your feedback!');
      skipQuestion('issue_reported_user');
    }, 100);
  };

  const cancelReport = () => {
    setShowReportModal(false);
    setReportDescription('');
  };

  const domains = [
    { 
      name: 'Sponsorship', 
      icon: FlagTriangleRight, 
      description: 'Leadership commitment and resource allocation for AI initiatives'
    },
    { 
      name: 'Technology', 
      icon: Cpu, 
      description: 'Data management, system integration, and technical infrastructure readiness'
    },
    { 
      name: 'People', 
      icon: Users, 
      description: 'Team capacity, skills, and cultural readiness for AI adoption'
    },
    { 
      name: 'Processes', 
      icon: Split, 
      description: 'Process documentation, standardization, and performance measurement capabilities'
    }
  ];

  const questions = [
    // Sponsorship Domain (5 questions)
    {
      domain: 0,
      question: "To what extent is there senior leadership interest in exploring AI or automation to improve your internal operations?",
      scores: [
        "No interest - leadership shows no curiosity or openness to AI/automation",
        "Minimal interest - leadership is aware of AI but shows little enthusiasm or support",
        "Moderate interest - leadership is curious about AI and open to learning more",
        "Strong interest - leadership actively seeks information and encourages AI exploration",
        "Full commitment - leadership champions AI initiatives and allocates resources for implementation"
      ]
    },
    {
      domain: 0,
      question: "To what degree is your organization prepared to allocate resources for exploring AI in internal operations?",
      scores: [
        "No willingness or ability to commit any resources for operational improvements",
        "Minimal willingness to allow occasional staff time only for operational AI",
        "Moderate commitment to allocate limited staff time (e.g. a one-time workshop) and minimal budget (less than $500)",
        "Substantial commitment with dedicated time and modest budget for operational improvements (less than $2000 or depending on a proven ROI)",
        "Full commitment with significant time allocation and appropriate budget for operational AI"
      ]
    },
    {
      domain: 0,
      question: "How would you characterize the timeline for exploring and potentially implementing AI in your operations?",
      scores: [
        "No urgency; purely theoretical interest",
        "Low urgency; willing to explore over the next 1-2 years",
        "Moderate urgency; aiming to implement within 6-12 months",
        "High urgency; seeking to implement within 3-6 months",
        "Critical urgency; looking to implement as soon as possible (within 1-3 months)"
      ]
    },
    {
      domain: 0,
      question: "Would your organization prefer to implement AI in operations by relying entirely on your own team, working with external specialists, or a combination of both?",
      scores: [
        "Exclusively internal exploration regardless of timeline",
        "Primarily self-directed with occasional external guidance",
        "Balanced approach using both internal resources and external expertise",
        "Primarily specialist-guided with significant internal involvement",
        "Fully specialist-led implementation to fast-track adoption and minimize risks"
      ]
    },
    {
      domain: 0,
      question: "Are executives or team leaders expressing frustration about operational inefficiencies, wasted resources, or falling behind competitors?",
      scores: [
        "No awareness - leadership doesn't recognize operational inefficiencies as issues",
        "Limited awareness - challenges are mentioned but not seen as priorities",
        "Growing awareness - leadership acknowledges pain points in specific areas",
        "Strong awareness - leadership is concerned about inefficiencies across multiple areas",
        "Critical awareness - urgent organization-wide recognition that inefficiencies require immediate action"
      ]
    },
    // Technology Domain (5 questions)
    {
      domain: 1,
      question: "How organized and accessible is your organizational knowledge?\n(Organizational Knowledge could include: project plans, lessons learned, best practices, past issues, training materials, research findings, meeting notes, decision rationale, subject matter expertise, ...)",
      scores: [
        "Scattered - knowledge exists mainly in people's heads with no central organization",
        "Disconnected - knowledge exists in separate systems with limited access to key information",
        "Partially organized - some knowledge is accessible but gaps remain in critical areas",
        "Well organized - most knowledge is accessible with good documentation practices",
        "Comprehensive - all knowledge is systematically organized and easily accessible to everyone"
      ]
    },
    {
      domain: 1,
      question: "How organized and accessible is your operational data?\n(This data could include customer records, transaction histories, performance metrics, HR data, financial records, inventory data)",
      scores: [
        "Operational data is completely scattered and inaccessible",
        "Operational data exists in multiple disconnected systems with limited access",
        "Some operational data is organized but significant portions remain difficult to access",
        "Most operational data is organized with reasonable accessibility",
        "Comprehensive operational data organization with excellent accessibility"
      ]
    },
    {
      domain: 1,
      question: "Do you have data quality management processes in place? If yes, how robust and enabled are they?",
      scores: [
        "No operational data quality measures exist",
        "Occasional ad hoc checks without formal process",
        "Basic quality rules applied inconsistently to operational data",
        "Regular quality processes for most critical operational data",
        "Comprehensive quality framework consistently applied to all operational data"
      ]
    },
    {
      domain: 1,
      question: "To what degree are your operational systems integrated with one another? Or how well do they work together and exchange data?\n(I mean systems like CRM, ERP, HR systems, project management tools, inventory systems)",
      scores: [
        "No integration capabilities between operational systems or we use several tools that are not connected with each other",
        "Limited ability to share data between only a few operational systems",
        "Moderate integration with some automated data flows between major systems",
        "Good integration capabilities with standardized data exchange across most systems or we have a main system that provides most of these functionalities",
        "Seamless integration with comprehensive data sharing across all operational systems or we have a single comprehensive system that covers all our operational workflows"
      ]
    },
    {
      domain: 1,
      question: "To what extent do your current operational tools or systems have AI capabilities, and how well are those capabilities being utilized?",
      scores: [
        "None of our operational tools have AI capabilities",
        "Some tools may have AI features but we're not aware of or using them",
        "We're aware of AI capabilities in some of our tools and occasionally use them",
        "We regularly use AI features in several of our operational systems",
        "We actively leverage advanced AI capabilities across multiple operational systems"
      ]
    },
    // People Domain (6 questions)
    {
      domain: 2,
      question: "How receptive is your team to adopting new technologies that would change how they work?",
      scores: [
        "Strong resistance to any operational change",
        "Cautious with significant concerns about new operational approaches",
        "Neutral attitude with willingness to consider operational changes",
        "Generally positive with enthusiasm from some team members about operational improvements",
        "Highly enthusiastic culture that actively seeks operational innovation"
      ]
    },
    {
      domain: 2,
      question: "How receptive is your team to learning new concepts, skills and tools in order to enhance operations and add value to their team?",
      scores: [
        "Resistant to learning new operational skills or concepts",
        "Reluctant learners who engage only when required",
        "Neutral attitude with basic willingness to learn operational improvements",
        "Actively engaged learners who seek opportunities to enhance operational skills",
        "Highly motivated continuous learners who proactively develop operational capabilities"
      ]
    },
    {
      domain: 2,
      question: "How much time can team members dedicate to engage in learning activities to make operational development effective?",
      scores: [
        "No time available for learning",
        "Minimal time (less than 1 hour weekly)",
        "Moderate time (1-2 hours weekly) or a one-time 4 hours course or workshop",
        "Substantial time (3-5 hours weekly) or a one-time 10 hours course or workshop",
        "Significant dedicated time (6+ hours weekly) or a one-time course or workshop longer than 10 hours"
      ]
    },
    {
      domain: 2,
      question: "How would you characterize the overall technical sophistication of your organization?",
      scores: [
        "Low-tech organization with minimal digital tool usage",
        "Basic digital literacy with standard office applications",
        "Moderate technical capabilities with some specialized systems",
        "Technically adept organization with various digital systems and some automation",
        "High-tech organization with advanced digital infrastructure and technical staff"
      ]
    },
    {
      domain: 2,
      question: "What level of technical expertise is available within your organization that could support operational development efforts?",
      scores: [
        "No technical expertise available within the organization",
        "Basic technical skills limited to everyday software use",
        "Intermediate technical skills with some understanding of systems integration",
        "Advanced technical skills including data management, API integration, and simple programming",
        "Expert technical skills including AI/ML experience, coding, and systems architecture"
      ]
    },
    {
      domain: 2,
      question: "How familiar is your team with no-code or low-code automation tools?\n(Tools like Zapier, Make, Airtable, Power Automate, etc.)",
      scores: [
        "No awareness of no-code/low-code tools",
        "Heard of these tools but never used them",
        "Basic experience with simple no-code automations",
        "Regular use of no-code tools for workflow improvements",
        "Advanced expertise building complex solutions with no-code platforms"
      ]
    },
    // Processes Domain (4 questions)
    {
      domain: 3,
      question: "How well documented are your key operational business processes?\n(These include customer onboarding, employee hiring, procurement, financial reporting, project management, inventory management, or sales processes)",
      scores: [
        "No operational documentation exists",
        "Minimal documentation covering only basic operations",
        "Moderate documentation of major operational processes",
        "Good documentation of most operational processes",
        "Comprehensive, clear documentation of all key operational processes"
      ]
    },
    {
      domain: 3,
      question: "Does your organization have a dedicated role or individuals responsible for managing and improving operational processes?",
      scores: [
        "No dedicated roles for process management at all",
        "Informal process management responsibilities occasionally assigned to staff",
        "Some employees have process improvement as a secondary responsibility",
        "Designated process owners for major operational areas",
        "Dedicated process management professionals or team focused on continuous operational improvement"
      ]
    },
    {
      domain: 3,
      question: "Do you have systems in place to monitor and measure operational productivity and efficiency?",
      scores: [
        "No operational metrics are defined or tracked",
        "Basic operational metrics are tracked manually and irregularly",
        "Several key operational metrics are tracked consistently but with limited analysis",
        "Comprehensive operational metrics framework with regular analysis and reporting",
        "Advanced operational performance measurement with real-time dashboards, automated analysis, and continuous improvement based on insights"
      ]
    },
    {
      domain: 3,
      question: "Does your organization perform process audits or reengineering to optimize operations?",
      scores: [
        "No process audits or reengineering activities are conducted",
        "Infrequent process reviews occur only when major problems arise",
        "Occasional process audits for some key operational areas with basic improvement efforts",
        "Regular process audits across most operational areas with structured reengineering initiatives",
        "Comprehensive, continuous process optimization program with systematic auditing and reengineering methodologies"
      ]
    }
  ];

  const analyzeResponse = async (response, questionIndex) => {
    // Ensure response is a string
    if (typeof response !== 'string') {
      console.error('Response is not a string:', response);
      throw new Error('Invalid response format');
    }
    
    const question = questions[questionIndex];
    const conversation = conversationHistory[questionIndex] || [];
    const clarificationCount = conversation.filter(msg => msg.role === 'assistant').length;
    
    // Check if choices were provided in this conversation and user responded with a number
    const choicesProvided = conversation.some(msg => 
      msg.role === 'assistant' && (
        msg.content.includes('Here are your response options:') ||
        msg.content.includes('1. ') || 
        msg.content.includes('Would one of these choices describe your case?')
      )
    );
    const responseIsNumber = /^[1-5]$/.test(response.trim());
    
    // If choices were provided and user gives a number, accept it directly
    if (choicesProvided && responseIsNumber) {
      // Mark this as self-scored since user chose from numbered options
      setScoringMethods(prev => ({
        ...prev,
        [questionIndex]: 'self'
      }));

      return { type: 'score', score: parseInt(response.trim()), confidence: 'HIGH' };
    }
    
    try {
      // Create a summarized context from previous conversation
      const conversationContext = conversation.length > 0 
        ? await summarizeContext(conversation)
        : '';

      // If we've reached 5 clarifications, make best judgment and move on
      if (clarificationCount >= 5) {
        // Force the LLM to make its best judgment by modifying the prompt
        const forceScorePrompt = `Based on all the conversation context above, make your best judgment and score this response. You MUST respond with SCORE:X CONFIDENCE:Y (where X is 1-5 and Y is HIGH/MEDIUM/LOW). Do not ask any more questions.

CURRENT QUESTION: "${question.question}"
${conversationContext}
USER'S LATEST RESPONSE: "${response}"

SCORING RUBRIC:
1 = ${question.scores[0]}
2 = ${question.scores[1]}
3 = ${question.scores[2]}
4 = ${question.scores[3]}
5 = ${question.scores[4]}

Make your best judgment and respond with SCORE:X CONFIDENCE:Y only.`;

        const apiResponse = await fetch(API_PROXY_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            prompt: forceScorePrompt
          })
        });

        const result = await apiResponse.json();
        
        if (result.response) {
          const scoreMatch = result.response.match(/SCORE:(\d)/);
          const confidenceMatch = result.response.match(/CONFIDENCE:(HIGH|MEDIUM|LOW)/i);
          if (scoreMatch) {
            const score = parseInt(scoreMatch[1]);
            const confidence = confidenceMatch ? confidenceMatch[1].toUpperCase() : 'LOW';
            return { type: 'score', score: score, confidence: confidence };
          }
        }
        
        // Fallback: return middle score if parsing fails
        return { type: 'score', score: 3, confidence: 'LOW' };
      }
      
      // If we've already asked 3 clarifying questions, automatically provide choices
      if (clarificationCount >= 3) {
        // Mark this question as having used "Provide Choices" since we're auto-providing them
        setUsedProvideChoices(prev => new Set([...prev, questionIndex]));
        
        return { 
          type: 'clarification', 
          message: `I understand this question might be unclear. Would one of these choices describe your case?
          1. ${question.scores[0]}
          2. ${question.scores[1]}
          3. ${question.scores[2]}
          4. ${question.scores[3]}
          5. ${question.scores[4]}
          
          You can respond with your choice's number or explain your specific answer if you're not sure which option fits best.` 
        };
      }

      const prompt = `You are conducting an AI readiness assessment. Your main job is to score the user by matching their responses to the closest of the choices in the Scoring Rubric.

RESPONSE LENGTH LIMIT: Keep ALL responses under 200 characters. Be concise and direct.

1. If you have enough info to score, respond with: SCORE:X CONFIDENCE:Y (where X is 1-5 and Y is HIGH/MEDIUM/LOW based on how confident you are in this scoring)
2. If the user's response is not clear or you need a specific detail to score accurately:
   - If their response contains a number (percentage, currency, count, etc.), make a reasonable assumption and ask for confirmation: "I assume you mean [interpretation], am I right?"
   - Otherwise, ask a brief question (max 20 words)
   - Be accepting of reasonable responses rather than asking for excessive clarification.
3. If user asks for clarification (e.g., "Clarify the question"): Respond with "Let me clarify: \n\n[simplified rephrasing that subtly incorporates key terms from the scoring rubric to guide their thinking]" (max 150 characters total)
4. If user asks for choices/options in any way (e.g., "show me options", "what are my choices", "provide choices"): Respond with exactly "SHOW_CHOICES"
5. CRITICAL: ONLY if choices were previously provided in this conversation for the user, and the user references any option by it's number (e.g., "5", "option 5", "option n.5", "option number 5", "choice 5", "I'd go with 5") or a keyword from it (e.g., partially, completely), immediately score it as SCORE:X CONFIDENCE:HIGH without asking questions. If choices were NOT provided, treat numbers as regular responses that may need clarification.


CURRENT QUESTION: "${question.question}"
${conversationContext}
USER'S LATEST RESPONSE: "${response}"

SCORING RUBRIC:
1 = ${question.scores[0]}
2 = ${question.scores[1]}
3 = ${question.scores[2]}
4 = ${question.scores[3]}
5 = ${question.scores[4]}`;

      const apiResponse = await fetch(API_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: prompt,
          max_tokens: 1000  // Increase token limit if the API supports it
        })
      });

      if (!apiResponse.ok) {
        throw new Error(`API request failed: ${apiResponse.status}`);
      }

      const data = await apiResponse.json();
      
      // Better error handling for API response structure
      if (!data.content || !Array.isArray(data.content) || data.content.length === 0) {
        throw new Error('Invalid API response structure');
      }
      
      const aiResponse = data.content[0].text.trim();
      
      if (aiResponse.includes('SCORE:')) {
        const scoreMatch = aiResponse.match(/SCORE:?\s*(\d)/);
        const confidenceMatch = aiResponse.match(/CONFIDENCE:\s*(HIGH|MEDIUM|LOW)/i);
        if (scoreMatch) {
          const score = parseInt(scoreMatch[1]);
          if (score >= 1 && score <= 5) {
            const confidence = confidenceMatch ? confidenceMatch[1].toUpperCase() : 'MEDIUM';
            return { type: 'score', score: score, confidence: confidence };
          }
        }
      }
      
      if (aiResponse.includes('SHOW_CHOICES')) {
        return { type: 'show_choices' };
      }
      
      let cleanResponse = aiResponse.replace(/SCORE:\d*/g, '').trim();
      
      // Check if response might be truncated using comprehensive pattern matching
      const naturalEndings = [
        /[.!?][")'\]]*$/,           // Punctuation + optional closing chars: year?) or said."
        /[.!?]{2,}$/,               // Multiple punctuation: really?! or interesting...
        /\.{3}$/,                   // Ellipsis: and so on...
        /:$/,                       // Colon endings: timeline:
        /\b(etc|e\.g|i\.e)\.$/i     // Common abbreviations: etc., e.g., i.e.
      ];
      const endsNaturally = naturalEndings.some(pattern => pattern.test(cleanResponse));
      const seemsTruncated = cleanResponse.length > 10 && !endsNaturally;
      
      if (seemsTruncated) {
        cleanResponse += '... (Response may have been cut off. Please try rephrasing your question.)';
      }
      
      return { type: 'clarification', message: cleanResponse };
      
    } catch (error) {
      console.error('API Error:', error);

      // For both regular responses and clarification requests, trigger the retry/choice system
      return { type: 'connection_error', error: error.message };
    }
  };

  // New retry logic with user choice
  const handleConnectionError = async (response, questionIndex) => {
    setConnectionState('retrying');
    setCurrentRetryAttempt(1);

    // First retry attempt
    try {
      console.log('Attempting first retry...');
      await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
      const result = await analyzeResponse(response, questionIndex);

      if (result.type !== 'connection_error') {
        setConnectionState('connected');
        setCurrentRetryAttempt(0);
        return result;
      }
    } catch (error) {
      console.log('First retry failed');
    }

    // After first retry fails, show user options while continuing to retry in background
    setConnectionState('user_choice');
    setShowConnectionOptions(true);
    userMadeChoiceRef.current = false; // Reset the choice flag

    // Start background retries automatically
    continueBackgroundRetries(response, questionIndex);

    return {
      type: 'retry_prompt',
      message: 'My AI brain is not responding... attempting reconnection (1/3)',
      questionIndex: questionIndex,
      userResponse: response
    };
  };

  // Handle user's choice when connection fails
  const handleUserConnectionChoice = async (choice, questionIndex, userResponse) => {
    if (choice === 'use_numbers') {
      // User chose numbered options - stop retries and show choices
      userMadeChoiceRef.current = true; // Signal background retries to stop
      setConnectionState('connected');
      setShowConnectionOptions(false);
      setRetryCountdown(0);

      setScoringMethods(prev => ({
        ...prev,
        [questionIndex]: 'self'
      }));

      const question = questions[questionIndex];
      const numberedResponse = `Here are your response options:

1. ${question.scores[0]}
2. ${question.scores[1]}
3. ${question.scores[2]}
4. ${question.scores[3]}
5. ${question.scores[4]}

Please respond with your choice's number (1-5) based on which option best describes your situation.`;

      // Add to conversation history
      const currentConversation = conversationHistory[questionIndex] || [];
      const updatedConversation = [
        ...currentConversation,
        { role: 'assistant', content: numberedResponse }
      ];

      setConversationHistory(prev => ({
        ...prev,
        [questionIndex]: updatedConversation
      }));

      return;
    }
  };

  // Continue retrying in background while user waits
  const continueBackgroundRetries = async (response, questionIndex) => {
    const maxRetries = 3;

    for (let attempt = 2; attempt <= maxRetries; attempt++) {
      // Check if user already made a choice - if so, stop retrying
      if (userMadeChoiceRef.current) {
        console.log('User made choice, stopping background retries');
        return;
      }

      setCurrentRetryAttempt(attempt);

      try {
        // 3 second delay with countdown
        const delay = 3000;
        setRetryCountdown(3);

        // Countdown timer
        for (let countdown = 3; countdown > 0; countdown--) {
          // Check if user made choice during countdown
          if (userMadeChoiceRef.current) {
            console.log('User made choice during countdown, stopping retries');
            return;
          }

          setRetryCountdown(countdown);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        // Check again after delay in case user made choice during wait
        if (userMadeChoiceRef.current) {
          console.log('User made choice after countdown, stopping retries');
          return;
        }

        setRetryCountdown(0);
        console.log(`Retry attempt ${attempt}/${maxRetries}`);
        const result = await analyzeResponse(response, questionIndex);

        if (result.type !== 'connection_error') {
          // Success! Mark as AI-scored and continue
          setScoringMethods(prev => ({
            ...prev,
            [questionIndex]: 'ai'
          }));

          setConnectionState('connected');
          setShowConnectionOptions(false);
          setCurrentRetryAttempt(0);
          setRetryCountdown(0);

          // Process the successful result
          if (result.type === 'score') {
            handleSuccessfulScore(result, questionIndex);
          } else if (result.type === 'clarification') {
            handleClarificationResponse(result, questionIndex);
          }
          return;
        }
      } catch (error) {
        console.log(`Retry attempt ${attempt} failed:`, error);
      }
    }

    // All retries failed - show final options
    setConnectionState('failed');
    setCurrentRetryAttempt(0);
    setRetryCountdown(0);
  };

  // Helper function to handle successful scoring
  const handleSuccessfulScore = (result, questionIndex) => {
    setScores(prev => ({
      ...prev,
      [questionIndex]: result.score
    }));

    setConfidenceLevels(prev => ({
      ...prev,
      [questionIndex]: result.confidence
    }));

    nextQuestion();
  };

  // Helper function to handle clarification responses
  const handleClarificationResponse = (result, questionIndex) => {
    const currentConversation = conversationHistory[questionIndex] || [];
    const updatedConversation = [
      ...currentConversation,
      { role: 'assistant', content: result.message }
    ];

    setConversationHistory(prev => ({
      ...prev,
      [questionIndex]: updatedConversation
    }));
  };

  // Helper function to move to next question
  const nextQuestion = () => {
    // End timer for current question
    endQuestionTimer(currentQuestionIndex);

    if (currentQuestionIndex < questions.length - 1) {
      const nextQuestionIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextQuestionIndex);
      // Start timer for next question
      startQuestionTimer(nextQuestionIndex);
    } else {
      setCurrentStage('results');
    }
  };


  const handleQuickAction = async (action) => {
    if (isProcessing) return;
    
    // Handle "Provide Choices" locally instead of sending to API
    if (action === "Provide Choices") {
      // Mark this question as having used "Provide Choices"
      setUsedProvideChoices(prev => new Set([...prev, currentQuestionIndex]));
      
      const question = questions[currentQuestionIndex];
      const choicesResponse = `Here are your response options:

1. ${question.scores[0]}
2. ${question.scores[1]}
3. ${question.scores[2]}
4. ${question.scores[3]}
5. ${question.scores[4]}

You can respond with your choice's number or explain your specific answer if you're not sure which option fits best.`;

      // Add the choices response to conversation history for current question
      const currentConversation = conversationHistory[currentQuestionIndex] || [];
      const updatedConversation = [
        ...currentConversation,
        { role: 'user', content: action },
        { role: 'assistant', content: choicesResponse }
      ];
      
      setConversationHistory(prev => ({
        ...prev,
        [currentQuestionIndex]: updatedConversation
      }));
      
      // Smooth scroll to textarea after providing choices
      setTimeout(() => {
        const textarea = document.querySelector('textarea');
        if (textarea) {
          // Calculate target scroll position
          const textareaRect = textarea.getBoundingClientRect();
          const targetY = window.scrollY + textareaRect.top - (window.innerHeight / 2) + (textareaRect.height / 2);
          
          // Smooth scroll using window.scrollTo
          window.scrollTo({
            top: targetY,
            behavior: 'smooth'
          });
          
          // Focus after scroll completes
          setTimeout(() => textarea.focus(), 1000);
        }
      }, 200);
      
      return;
    }
    
    setCurrentResponse(action);
    await handleResponseSubmit(action);
  };

  const handleResponseSubmit = async (quickAction = null) => {
    const responseText = quickAction || (currentResponse && typeof currentResponse === 'string' ? currentResponse.trim() : '');
    if (!responseText || isProcessing) return;
    
    setIsProcessing(true);
    
    try {
      const currentConversation = conversationHistory[currentQuestionIndex] || [];
      const updatedConversation = [...currentConversation, { role: 'user', content: responseText }];
      
      const result = await analyzeResponse(responseText, currentQuestionIndex);

      // Handle connection errors with retry logic
      if (result.type === 'connection_error') {
        // Don't update conversation history yet, let retry logic handle it
        const retryResult = await handleConnectionError(responseText, currentQuestionIndex);

        if (retryResult.type === 'score' || retryResult.type === 'clarification') {
          // First retry succeeded, process normally
          if (retryResult.type === 'score') {
            setScoringMethods(prev => ({
              ...prev,
              [currentQuestionIndex]: 'ai'
            }));
            handleSuccessfulScore(retryResult, currentQuestionIndex);
          } else if (retryResult.type === 'clarification') {
            updatedConversation.push({ role: 'assistant', content: retryResult.message });
            setConversationHistory(prev => ({
              ...prev,
              [currentQuestionIndex]: updatedConversation
            }));
            setWaitingForClarification(true);
          }
        } else if (retryResult.type === 'retry_prompt') {
          // Show user options - UI will handle this
          setConversationHistory(prev => ({
            ...prev,
            [currentQuestionIndex]: updatedConversation
          }));
        }

        setCurrentResponse('');
        setIsProcessing(false);
        return;
      } else if (result.type === 'clarification') {
        setWaitingForClarification(true);
        updatedConversation.push({ role: 'assistant', content: result.message });
        
        setConversationHistory(prev => ({
          ...prev,
          [currentQuestionIndex]: updatedConversation
        }));
        
        setCurrentResponse('');
        
        // Smooth scroll to textarea after clarification response
        setTimeout(() => {
          const textarea = document.querySelector('textarea');
          if (textarea) {
            // Calculate target scroll position
            const textareaRect = textarea.getBoundingClientRect();
            const targetY = window.scrollY + textareaRect.top - (window.innerHeight / 2) + (textareaRect.height / 2);
            
            // Smooth scroll using window.scrollTo
            window.scrollTo({
              top: targetY,
              behavior: 'smooth'
            });
            
            // Focus after scroll completes
            setTimeout(() => textarea.focus(), 1000);
          }
        }, 200);
      } else if (result.type === 'show_choices') {
        // Generate and display choices locally
        const question = questions[currentQuestionIndex];
        const choicesResponse = `Here are your response options:

1. ${question.scores[0]}
2. ${question.scores[1]}
3. ${question.scores[2]}
4. ${question.scores[3]}
5. ${question.scores[4]}

You can respond with your choice's number or explain your specific answer if you're not sure which option fits best.`;

        updatedConversation.push({ role: 'assistant', content: choicesResponse });
        
        setConversationHistory(prev => ({
          ...prev,
          [currentQuestionIndex]: updatedConversation
        }));
        
        setCurrentResponse('');
      } else if (result.type === 'score') {
        setWaitingForClarification(false);

        // Mark as AI-scored (successful AI analysis)
        setScoringMethods(prev => ({
          ...prev,
          [currentQuestionIndex]: 'ai'
        }));

        setResponses(prev => ({
          ...prev,
          [currentQuestionIndex]: responseText
        }));

        setScores(prev => ({
          ...prev,
          [currentQuestionIndex]: result.score || result.value
        }));

        setConfidenceLevels(prev => ({
          ...prev,
          [currentQuestionIndex]: result.confidence || 'MEDIUM'
        }));

        setCurrentResponse('');

        // End timer for current question
        endQuestionTimer(currentQuestionIndex);

        if (currentQuestionIndex < questions.length - 1) {
          const nextQuestionIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextQuestionIndex);
          // Start timer for next question
          startQuestionTimer(nextQuestionIndex);
        } else {
          setCurrentStage('results');
        }
      }
    } catch (error) {
      console.error('Error processing response:', error);
      if (error.message.includes('529')) {
        alert('The AI service is temporarily overloaded. Your response will be scored using backup scoring. You can continue with the assessment.');
      } else {
        alert(`There was an error processing your response: ${error.message}. Please try again.`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const calculateDomainScores = () => {
    const domainScores = [0, 0, 0, 0];
    const domainCounts = [0, 0, 0, 0];
    
    questions.forEach((question, index) => {
      if (scores[index]) {
        domainScores[question.domain] += scores[index];
        domainCounts[question.domain]++;
      }
    });
    
    return domainScores.map((total, index) => 
      domainCounts[index] > 0 ? Math.floor(total / domainCounts[index]) : 0
    );
  };

  const storeAssessmentData = async (assessmentData, status = 'completed') => {
    console.log('🗄️ Storing assessment data with status:', status);
    try {
      // Build complete assessment data for storage including qualification info
      const completeAssessmentData = {
        ...assessmentData,
        submission_id: assessmentData.submissionId, // Convert to snake_case for server
        qualification: {
          userPosition: userPosition,
          userPositionOther: userPositionOther,
          oversightAreas: oversightAreas,
          isQualified: isQualified,
          qualificationTimestamp: assessmentData.timestamp
        },
        conversationHistory: conversationHistory,
        status: status
      };

      const response = await fetch(STORAGE_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(completeAssessmentData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Assessment data stored successfully:', result.file_path);
        return result;
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to store assessment data:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error storing assessment data:', error);
    }
  };

  const sendResultsEmail = async (assessmentData) => {
    console.log('📧 Sending results email...');
    try {
      const emailData = {
        name: userName.trim(),
        email: userEmail.trim(),
        phone: userPhone.trim(),
        organization: userOrganization.trim(),
        submission_id: assessmentData.submissionId,
        timestamp: assessmentData.timestamp,
        overall_score: assessmentData.scores ? assessmentData.scores.overall : 0,
        readiness_level: assessmentData.scores ? assessmentData.scores.readinessLevel : 'Not Available',
        sponsorship_score: assessmentData.scores ? assessmentData.scores.domains.sponsorship : 0,
        technology_score: assessmentData.scores ? assessmentData.scores.domains.technology : 0,
        people_score: assessmentData.scores ? assessmentData.scores.domains.people : 0,
        processes_score: assessmentData.scores ? assessmentData.scores.domains.processes : 0
      };

      const response = await fetch(EMAIL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Results email sent successfully:', result.email);
        return { success: true, result };
      } else {
        const errorText = await response.text();
        console.error('❌ Email API failed:', response.status, errorText);
        return { success: false, error: `HTTP ${response.status}: ${errorText}` };
      }
    } catch (error) {
      console.error('❌ Email sending failed:', error);
      return { success: false, error: error.message };
    }
  };

  const sendToNotion = async (assessmentData, status = 'completed') => {
    try {
      // Format data for Notion database - handles both initial capture and completion
      const notionData = {
        submission_id: assessmentData.submissionId,
        name: userName.trim(),
        email: userEmail.trim(),
        phone: userPhone.trim(),
        organization: userOrganization.trim(),
        position: userPosition === 'other' ? userPositionOther.trim() : userPosition,
        oversight: oversightAreas.join(', '),
        isQualified: isQualified,
        title: `${userName.trim()} - ${userOrganization.trim()}`,
        timestamp: assessmentData.timestamp,
        status: status,
        completion_percentage: assessmentData.completionStatus ? assessmentData.completionStatus.completionPercentage : 0,
        total_time_minutes: assessmentData.timing ? Math.round(assessmentData.timing.totalTimeSeconds / 60) : 0,
        total_time_seconds: assessmentData.timing ? assessmentData.timing.totalTimeSeconds : 0,
        overall_score: assessmentData.scores ? assessmentData.scores.overall : 0,
        readiness_level: assessmentData.scores ? assessmentData.scores.readinessLevel : 'Not Started',
        sponsorship_score: assessmentData.scores ? assessmentData.scores.domains.sponsorship : 0,
        technology_score: assessmentData.scores ? assessmentData.scores.domains.technology : 0,
        people_score: assessmentData.scores ? assessmentData.scores.domains.people : 0,
        processes_score: assessmentData.scores ? assessmentData.scores.domains.processes : 0,
        total_questions: assessmentData.completionStatus ? assessmentData.completionStatus.totalQuestions : questions.length,
        answered_questions: assessmentData.completionStatus ? assessmentData.completionStatus.answeredQuestions : 0,
        skipped_questions: assessmentData.skipAndIssueData ? assessmentData.skipAndIssueData.totalSkipped : 0,
        reported_issues: assessmentData.skipAndIssueData ? assessmentData.skipAndIssueData.totalReported : 0,
        average_question_time: assessmentData.timing ? assessmentData.timing.averageQuestionTimeSeconds : 0,
        confidence_data: assessmentData.confidenceData || null,
        low_confidence_scores: assessmentData.lowConfidenceScores || null
      };

      
      const response = await fetch(NOTION_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(notionData)
      });
      
      const responseText = await response.text();
      if (!response.ok) {
        console.error('Notion API failed:', response.status, responseText);
      }
      
      // Also store complete assessment data (don't block Notion submission if this fails)
      if (status !== 'initial_capture') {
        console.log('📊 Sending to storage with status:', status);
        try {
          await storeAssessmentData(assessmentData, status);
        } catch (storageError) {
          console.error('❌ Storage failed but continuing:', storageError);
        }
      } else {
        console.log('⏭️ Skipping storage for initial_capture');
      }
    } catch (error) {
    }
  };

  const renderWelcome = () => (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #203659 0%, #333488 100%)' }}>
      {/* Header Section */}
      <div className="pt-8 pb-16 text-center text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <a 
            href="https://upskillingcircle.com" 
            className="inline-block mb-8 hover:opacity-90 transition-all duration-300 transform hover:scale-105"
          >
            <img 
              src="/upskilling logo - horizontal - dark bg.png" 
              alt="Upskilling Circle - Where Human Potential Meets Intelligent Technology" 
              className="h-16 w-auto cursor-pointer mx-auto"
            />
          </a>
          
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ 
              background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              AI Readiness Assessment
            </h1>
            <p className="text-lg text-gray-200 leading-relaxed mb-6">
              A comprehensive organizational self-assessment in conversational format
            </p>
            <div className="flex justify-center items-center gap-6 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                <span>20 Questions</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                <span>10-15 Minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                <span>Personalized Report</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-full md:max-w-4xl mx-auto px-4 py-6 md:p-6 space-y-6 relative -mt-4 md:-mt-8">
          <div className="bg-white rounded-2xl shadow-2xl p-4 md:p-8 space-y-8" style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
      
            {/* Assessment Overview */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl border-l-4 border-l-orange-400 shadow-lg">
              <div className="text-center">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ 
                    background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)' 
                  }}>
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold" style={{color: '#203659'}}>Assessment Overview</h2>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed text-center">
                Discover your organization's AI transformation potential and unlock operational efficiency gains of up to 40%. This strategic assessment evaluates your readiness across <strong>four critical domains</strong> and provides an actionable roadmap to reduce costs, accelerate processes, and gain competitive advantage through intelligent automation.
              </p>
            </div>

            {/* AI-Powered & Research-Based Section */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-xl border-l-4 border-l-blue-400 shadow-lg">
              <div className="text-center">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ 
                    background: 'linear-gradient(45deg, #3B82F6, #8B5CF6)' 
                  }}>
                    <span className="text-white font-bold text-lg">AI</span>
                  </div>
                  <h3 className="text-2xl font-bold" style={{color: '#203659'}}>AI-Powered Intelligence</h3>
                </div>
                <p className="text-gray-700 leading-relaxed max-w-3xl mx-auto">
                  This assessment leverages advanced artificial intelligence to provide personalized, conversational evaluation of your organization's readiness. Built on <strong>extensive research</strong> from leading AI implementation studies and best practices from hundreds of successful transformations, our AI guide adapts to your unique context and provides insights tailored specifically to your organization's needs.
                </p>
              </div>
            </div>

            {/* Quick CTA Section */}
            <div className="text-center py-6">
              <div className="flex items-center justify-center gap-5">
                <span className="text-lg font-medium text-gray-600">Ready to start?</span>
                <button
                  onClick={() => setShowUserInfoModal(true)}
                  className="text-white px-6 py-2.5 rounded-lg transition-all duration-300 inline-flex items-center gap-2 transform hover:scale-105 shadow-md text-base"
                  style={{
                    background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)',
                    boxShadow: '0 2px 10px rgba(245, 138, 31, 0.2)'
                  }}
                >
                  Begin Assessment
                  <ChevronRight className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-500">or continue reading</span>
              </div>
            </div>

            {/* Free Report Section */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl border-l-4 border-l-green-400 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#10B981' }}>
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold" style={{color: '#a2c5c1'}}>Personalized Report</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="md:col-span-2">
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Upon completion, you'll receive a comprehensive report containing:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: '#10B981' }}></div>
                    <span className="text-gray-700">Overall readiness score (1-20 scale) with classification</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: '#10B981' }}></div>
                    <span className="text-gray-700">Domain-specific scores for Sponsorship, Technology, People & Processes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: '#10B981' }}></div>
                    <span className="text-gray-700">Prioritized focus areas based on your weakest domains</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: '#10B981' }}></div>
                    <span className="text-gray-700">Curated resources and tools for each improvement area</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: '#10B981' }}></div>
                    <span className="text-gray-700">Strategic implementation roadmap with timeline</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: '#10B981' }}></div>
                    <span className="text-gray-700">Executive summary for leadership presentation</span>
                  </div>
                  </div>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <img 
                    src="/readiness assessment report cover mockup.png"
                    alt="AI Readiness Assessment Report Preview" 
                    className="w-full max-w-sm rounded-lg"
                  />
                </div>
              </div>
              <div className="mt-4 p-4 bg-green-100 rounded-lg">
                <p className="text-sm text-green-800 font-medium text-center">
                  💡 The quality of your report depends on the honesty and accuracy of your responses
                </p>
              </div>
            </div>


            {/* Assessment Domains */}
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2" style={{color: '#203659'}}>Assessment Domains</h3>
                <p className="text-gray-600">We'll evaluate your organization across four critical areas</p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {domains.map((domain, index) => {
                  const Icon = domain.icon;
                  return (
                    <div key={index} className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-xl border shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ 
                          background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)' 
                        }}>
                          <Icon className="w-6 h-6 text-white" style={domain.name === 'Processes' ? {transform: 'rotate(90deg)'} : {}} />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-bold mb-2" style={{color: '#203659'}}>{domain.name}</h4>
                          <p className="text-gray-600 leading-relaxed">{domain.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2" style={{color: '#203659'}}>Frequently Asked Questions</h3>
                <p className="text-gray-600">Everything you need to know about the assessment</p>
              </div>
              {/* Mobile: Single column accordion */}
              <div className="md:hidden space-y-4">
                {faqData.map((faq) => (
                  <div key={faq.id} className="bg-white rounded-xl border shadow-lg overflow-hidden">
                    <div
                      className="p-6 cursor-pointer hover:bg-gray-50 transition-all duration-200 border-l-4 border-transparent hover:border-orange-300"
                      onClick={() => toggleFAQ(faq.id)}
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-bold pr-4" style={{color: '#203659'}}>{faq.question}</h4>
                        <ChevronDown
                          className={`w-5 h-5 faq-chevron ${expandedFAQs[faq.id] ? 'rotated' : ''} flex-shrink-0`}
                          style={{color: '#F58A1F'}}
                        />
                      </div>
                      <div className={`faq-answer ${expandedFAQs[faq.id] ? 'expanded' : ''}`}>
                        <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: 2x2 Grid static cards (original style) */}
              <div className="hidden md:grid md:grid-cols-2 gap-6">
                {faqData.map((faq) => (
                  <div key={faq.id} className="bg-white p-6 rounded-xl border shadow-lg">
                    <h4 className="text-lg font-bold mb-3" style={{color: '#203659'}}>{faq.question}</h4>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div id="main-cta" className="text-center space-y-6 pt-4">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
                <p className="text-2xl font-bold mb-1" style={{color: '#203659'}}>Take the First Step Toward AI Transformation</p>
                <p className="text-gray-600 mb-4">Get clarity on your organization's AI readiness and receive a personalized roadmap for success</p>
                
                {/* Start Assessment Button */}
                <div className="text-center">
                  <button
                    onClick={() => setShowUserInfoModal(true)}
                    className="text-white px-12 py-4 rounded-xl text-lg font-bold transition-all duration-300 flex items-center gap-3 mx-auto shadow-2xl transform hover:scale-105 cursor-pointer"
                    style={{
                      background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)',
                      boxShadow: '0 8px 25px rgba(245, 138, 31, 0.4)'
                    }}
                  >
                    Start Assessment
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUserInfoModal = () => {
    const validateEmail = (email) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    const handleEmailChange = (e) => {
      const email = e.target.value;
      setUserEmail(email);
      if (email.trim()) {
        setIsEmailValid(validateEmail(email));
      } else {
        setIsEmailValid(true); // Don't show error for empty field
      }
    };

    const handleSubmit = async () => {
      if (!userName.trim() || !userOrganization.trim() || !userEmail.trim()) {
        return;
      }
      if (!validateEmail(userEmail.trim())) {
        setIsEmailValid(false);
        return;
      }
      
      // Start transition loading state
      setIsTransitioning(true);
      
      // Generate Assessment ID
      const now = new Date();
      const assessmentId = now.getFullYear().toString() + 
                         (now.getMonth() + 1).toString().padStart(2, '0') +
                         now.getDate().toString().padStart(2, '0') +
                         now.getHours().toString().padStart(2, '0') +
                         now.getMinutes().toString().padStart(2, '0') +
                         now.getSeconds().toString().padStart(2, '0');
      setSubmissionId(assessmentId);

      // Send initial user info to Notion immediately
      const initialData = {
        submissionId: assessmentId,
        timestamp: new Date().toISOString()
      };

      try {
        await sendToNotion(initialData, 'initial_capture');
      } catch (error) {
        console.error('Failed to send initial user info:', error);
        // Don't block the user experience
      }

      // Start modal fade-out animation
      setModalClosing(true);
      
      // Wait for fade-out, then close modal and show qualification
      setTimeout(() => {
        setShowUserInfoModal(false);
        setModalClosing(false);
        setIsTransitioning(false);
        setCurrentStage('qualification');
      }, 400);
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        // Provide feedback if validation fails
        if (!userName.trim()) {
          // Find and focus the name field
          const nameField = document.querySelector('input[placeholder*="full name"]');
          nameField?.focus();
          return;
        }
        if (!userOrganization.trim()) {
          // Find and focus the organization field
          const orgField = document.querySelector('input[placeholder*="organization"]');
          orgField?.focus();
          return;
        }
        if (!userEmail.trim()) {
          // Find and focus the email field
          const emailField = document.querySelector('input[placeholder*="email"]');
          emailField?.focus();
          return;
        }
        if (!validateEmail(userEmail.trim())) {
          // Set email validation error and focus email field
          setIsEmailValid(false);
          const emailField = document.querySelector('input[type="email"]');
          emailField?.focus();
          return;
        }
        
        handleSubmit();
      }
    };


    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ${modalClosing ? 'fade-out-animation' : ''}`}>
        <div className={`bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto ${modalClosing ? 'fade-out-animation' : ''}`}>
          <div className="p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">First, let's get to know you</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter your full name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">the one you would like to assess for AI readiness</p>
              <input
                type="text"
                placeholder="Enter your organization name"
                value={userOrganization}
                onChange={(e) => setUserOrganization(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                placeholder="Enter your email address"
                value={userEmail}
                onChange={handleEmailChange}
                onKeyPress={handleKeyPress}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                  isEmailValid 
                    ? 'border-gray-300 focus:ring-orange-500' 
                    : 'border-red-500 focus:ring-red-500'
                }`}
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              />
              {!isEmailValid && (
                <p className="text-red-500 text-sm mt-1">Please enter a valid email address</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number (optional)
              </label>
              <p className="text-xs text-gray-500 mb-2">with country code</p>
              <input
                type="tel"
                placeholder="e.g. +1 555-123-4567"
                value={userPhone}
                onChange={(e) => setUserPhone(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              />
            </div>
          </div>
          
          <div className="text-center mt-6 mb-4">
            <p className="text-xs text-gray-500">You're not subscribing to anything yet. <br/> This is just to receive your assessment report.</p>
          </div>
          
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => setShowUserInfoModal(false)}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!userName.trim() || !userOrganization.trim() || !userEmail.trim() || !isEmailValid || isTransitioning}
              className={`flex-1 px-4 py-3 rounded-lg text-white font-medium transition-all flex items-center justify-center gap-2 ${
                (!userName.trim() || !userOrganization.trim() || !userEmail.trim() || !isEmailValid || isTransitioning)
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'transform hover:scale-105 cursor-pointer'
              }`}
              style={(!userName.trim() || !userOrganization.trim() || !userEmail.trim() || !isEmailValid || isTransitioning) 
                ? {} 
                : { 
                    background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)',
                    boxShadow: '0 4px 15px rgba(245, 138, 31, 0.4)'
                  }
              }
            >
              {isTransitioning ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Preparing Assessment...
                </>
              ) : (
                'Start Assessment'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderDisqualified = () => {
    const handleNotifyMe = () => {
      // TODO: Send notification request to backend
      setShowWaitingListConfirmation(true);
    };

    const handleShareWithManager = () => {
      setShowEmailTemplate(!showEmailTemplate);
    };

    const copyEmailTemplate = () => {
      const emailTemplate = `Subject: AI Readiness Assessment - Leadership Input Needed

Hi [Manager Name],

I came across this AI Readiness Assessment that could provide valuable insights into our organization's readiness for AI implementation.

Since you have oversight across multiple business areas, your perspective would be ideal for completing this assessment and getting accurate results for our team.

Assessment Link: https://assess.upskillingcircle.com

It takes about 10-15 minutes and provides a comprehensive report on our AI readiness across key domains.

Best regards,
${userName}`;
      
      navigator.clipboard.writeText(emailTemplate).then(() => {
        alert('Email template copied to clipboard!');
        setShowEmailTemplate(false);
      }).catch(() => {
        alert('Please manually copy the email template above.');
      });
    };

    return (
      <div className={`max-w-4xl mx-auto p-4 space-y-4 ${assessmentStarting ? 'fade-out-animation' : 'fade-in-animation'}`}>

        {/* Main Content Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Assessment Recommendation</h3>
            <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
              Based on your responses, we recommend having a <strong>senior leader</strong> or someone with <strong>comprehensive organizational knowledge</strong> complete this assessment for the most accurate results.
            </p>
          </div>

          {/* Three Options */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Option 1: Share with Manager */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center flex flex-col h-full">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-green-800 mb-2">Share with a Manager</h4>
              <p className="text-green-700 text-sm mb-4 flex-grow">Get a senior leader to complete this assessment for comprehensive organizational insights.</p>
              <button
                onClick={handleShareWithManager}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm w-full mt-auto"
              >
                Get Email Template
              </button>
            </div>

            {/* Option 2: Personal Assessment */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center flex flex-col h-full">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-blue-800 mb-2">Personal Assessment</h4>
              <p className="text-blue-700 text-sm mb-4 flex-grow">We're developing a Personal Readiness Assessment for individual contributors. Get notified when it's ready.</p>
              <button
                onClick={handleNotifyMe}
                disabled={showWaitingListConfirmation}
                className={`px-4 py-2 rounded-lg font-medium text-sm w-full mt-auto transition-colors ${
                  showWaitingListConfirmation 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {showWaitingListConfirmation ? 'Added to Waiting List' : 'Notify Me When Ready'}
              </button>
            </div>

            {/* Option 3: Continue Anyway */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center flex flex-col h-full">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-orange-800 mb-2">Continue Anyway</h4>
              <p className="text-orange-700 text-sm mb-4 flex-grow">Proceed with the assessment using your perspective. Results may be less comprehensive.</p>
              <button
                onClick={() => {
                  setIsQualified(true);
                  // Start assessment transition animation
                  setAssessmentStarting(true);
                  setTimeout(() => {
                    setCurrentStage('assessment');
                    startAssessmentTimer();
                    // Keep animation class active for the duration of the animation
                    setTimeout(() => {
                      setAssessmentStarting(false);
                    }, 800);
                  }, 800); // Same prominent transition as from Q2
                }}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium text-sm w-full mt-auto"
              >
                Start Assessment
              </button>
            </div>
          </div>

          {/* Waiting List Confirmation */}
          {showWaitingListConfirmation && (
            <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    <strong>You're on the waiting list!</strong> We'll notify you at <strong>{userEmail}</strong> when the Personal Readiness Assessment is ready.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Email Template Section */}
          {showEmailTemplate && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold text-gray-800">Email Template</h4>
                <button
                  onClick={copyEmailTemplate}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                >
                  Copy Template
                </button>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4 text-left text-sm text-gray-800 font-mono leading-relaxed">
                <div className="mb-2"><strong>Subject:</strong> AI Readiness Assessment - Leadership Input Needed</div>
                <div className="mb-3"><strong>Body:</strong></div>
                <div>Hi [Manager Name],<br/><br/>
                I came across this AI Readiness Assessment that could provide valuable insights into our organization's readiness for AI implementation.<br/><br/>
                Since you have oversight across multiple business areas, your perspective would be ideal for completing this assessment and getting accurate results for our team.<br/><br/>
                Assessment Link: https://assess.upskillingcircle.com<br/><br/>
                It takes about 10-15 minutes and provides a comprehensive report on our AI readiness across key domains.<br/><br/>
                Best regards,<br/>
                {userName}</div>
              </div>
            </div>
          )}

          {/* Return to Homepage Button */}
          <div className="text-center">
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              Return to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderQualification = () => {
    const roleOptions = [
      { value: 'founder', label: 'Founder / Co-founder', qualified: true },
      { value: 'ceo', label: 'CEO / C-level Executive (COO, CTO, CIO, CHRO, etc.)', qualified: true },
      { value: 'senior_manager', label: 'Senior Manager / Department Head with cross-functional oversight', qualified: true },
      { value: 'other', label: 'Other (please specify)', qualified: false }
    ];

    const oversightOptions = [
      { value: 'strategy', label: 'Organizational Strategy' },
      { value: 'operations', label: 'Operations & Processes' },
      { value: 'technology', label: 'Technology & Data Infrastructure' },
      { value: 'people', label: 'People & Culture / HR' },
      { value: 'customer', label: 'Customer Experience & Market Position' }
    ];

    const handleOversightChange = (areaValue) => {
      setOversightAreas(prev => {
        if (prev.includes(areaValue)) {
          return prev.filter(area => area !== areaValue);
        } else {
          return [...prev, areaValue];
        }
      });
    };

    const handleQualificationSubmit = () => {
      const roleQualified = roleOptions.find(opt => opt.value === userPosition)?.qualified || false;
      const oversightQualified = oversightAreas.length >= 3; // Need at least 3 areas
      
      const qualified = roleQualified && oversightQualified;
      setIsQualified(qualified);

      // Start final question slide-out animation
      setQuestionAnimating(true);
      
      if (qualified) {
        // Longer, more prominent transition to assessment
        setAssessmentStarting(true);
        setTimeout(() => {
          setCurrentStage('assessment');
          startAssessmentTimer();
          setQuestionAnimating(false);
          // Keep animation class active for the duration of the animation
          setTimeout(() => {
            setAssessmentStarting(false);
          }, 800);
        }, 800); // Longer delay for prominent transition
      } else {
        // Standard transition to disqualified page
        setTimeout(() => {
          setCurrentStage('disqualified');
          setQuestionAnimating(false);
        }, 400);
      }
    };


    return (
      <div className={`max-w-4xl mx-auto p-4 space-y-4 ${assessmentStarting ? 'fade-out-animation' : 'fade-in-animation'}`}>

        {/* Progress indicator */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              {[0, 1].map((index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full ${
                    index < qualificationQuestionIndex ? 'bg-green-500' :
                    index === qualificationQuestionIndex ? 'bg-orange-500' :
                    'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <div className="text-sm text-gray-600">
              Question {qualificationQuestionIndex + 1} of 2
            </div>
          </div>
        </div>

        {/* Main Content Card - matching assessment design */}
        <div className="bg-white rounded-xl shadow-lg p-8 slide-in-from-bottom">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#203659'}}>
              Qualification Assessment
            </h2>
            <p className="text-gray-600">
              To ensure the most accurate assessment results, we need to verify your qualification to represent <strong>{userOrganization}</strong>.
            </p>
          </div>

          {/* Question 1: Position */}
          {qualificationQuestionIndex === 0 && (
            <div className={`bg-white p-6 rounded-xl border border-blue-100 min-h-[400px] ${questionAnimating ? 'slide-out-up' : 'slide-in-from-bottom'}`}>
              <div className="flex items-start gap-4 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2" style={{color: '#203659'}}>
                    Which best describes your current role?
                  </h3>
                  <p className="text-gray-600 text-sm">
                    This helps us understand your organizational oversight level.
                  </p>
                </div>
              </div>
              <div className="space-y-3 ml-12">
                {roleOptions.map((option) => (
                  <label key={option.value} className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                    userPosition === option.value 
                      ? 'border-orange-500 bg-orange-50 shadow-md' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="position"
                      value={option.value}
                      checked={userPosition === option.value}
                      onChange={(e) => setUserPosition(e.target.value)}
                      className="mr-4 w-5 h-5 text-orange-500 focus:ring-orange-500 focus:ring-2"
                    />
                    <span className={`font-medium ${
                      userPosition === option.value ? 'text-orange-700' : 'text-gray-700'
                    }`}>
                      {option.label}
                    </span>
                  </label>
                ))}
                
                {/* Text input for "Other" option */}
                {userPosition === 'other' && (
                  <div className="mt-4">
                    <input
                      type="text"
                      placeholder="Please specify your role..."
                      value={userPositionOther}
                      onChange={(e) => setUserPositionOther(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && userPositionOther.trim()) {
                          // Trigger the next button functionality
                          setQuestionAnimating(true);
                          setTimeout(() => {
                            setQualificationQuestionIndex(1);
                            setQuestionAnimating(false);
                          }, 300);
                        }
                      }}
                      className="w-full px-4 py-3 border-2 border-orange-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 focus:outline-none transition-all"
                      autoFocus
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Question 2: Domain Knowledge */}
          {qualificationQuestionIndex === 1 && (
            <div className={`bg-white p-6 rounded-xl border border-blue-100 min-h-[400px] ${questionAnimating ? 'slide-out-up' : 'slide-in-from-bottom'}`}>
              <div className="flex items-start gap-4 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2" style={{color: '#203659'}}>
                    Do you have direct oversight or reliable knowledge of the following areas?
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    <em>Check all that apply</em>
                  </p>
                </div>
              </div>
              <div className="space-y-3 ml-12">
                {oversightOptions.map((option) => (
                  <label key={option.value} className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                    oversightAreas.includes(option.value)
                      ? 'border-green-500 bg-green-50 shadow-md' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}>
                    <input
                      type="checkbox"
                      name="oversight"
                      value={option.value}
                      checked={oversightAreas.includes(option.value)}
                      onChange={() => handleOversightChange(option.value)}
                      className="mr-4 w-5 h-5 text-green-500 focus:ring-green-500 focus:ring-2 rounded"
                    />
                    <span className={`font-medium ${
                      oversightAreas.includes(option.value) ? 'text-green-700' : 'text-gray-700'
                    }`}>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-8">
            {/* Previous Button */}
            <button
              onClick={() => setQualificationQuestionIndex(prev => Math.max(0, prev - 1))}
              disabled={qualificationQuestionIndex === 0}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                qualificationQuestionIndex === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer'
              }`}
            >
              ← Previous
            </button>

            {/* Next/Submit Button */}
            {qualificationQuestionIndex === 0 ? (
              <button
                onClick={() => {
                  if (!userPosition || (userPosition === 'other' && !userPositionOther.trim())) return;
                  
                  setQuestionAnimating(true);
                  // Wait for slide-out animation, then change question
                  setTimeout(() => {
                    setQualificationQuestionIndex(1);
                    setQuestionAnimating(false);
                  }, 300);
                }}
                disabled={!userPosition || (userPosition === 'other' && !userPositionOther.trim())}
                className={`px-8 py-3 rounded-xl text-white font-bold transition-all duration-300 ${
                  !userPosition || (userPosition === 'other' && !userPositionOther.trim())
                    ? 'bg-gray-400 cursor-not-allowed opacity-70' 
                    : 'transform hover:scale-105 hover:shadow-xl cursor-pointer'
                }`}
                style={!userPosition || (userPosition === 'other' && !userPositionOther.trim())
                  ? {} 
                  : { 
                      background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)',
                      boxShadow: '0 8px 25px rgba(245, 138, 31, 0.4)'
                    }
                }
              >
                {!userPosition 
                  ? 'Please select your position' 
                  : (userPosition === 'other' && !userPositionOther.trim()) 
                    ? 'Please specify your role' 
                    : 'Next →'}
              </button>
            ) : (
              <button
                onClick={handleQualificationSubmit}
                className="px-8 py-3 rounded-xl text-white font-bold transition-all duration-300 transform hover:scale-105 hover:shadow-xl cursor-pointer"
                style={{ 
                  background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)',
                  boxShadow: '0 8px 25px rgba(245, 138, 31, 0.4)'
                    }
                }
              >
                Continue to Assessment
              </button>
            )}
          </div>

        </div>
      </div>
    );
  };

  const renderAssessment = () => {
    const question = questions[currentQuestionIndex];
    
    // Guard clause: if no question found, show loading or error
    if (!question) {
      return (
        <div className="max-w-4xl mx-auto p-4 space-y-4">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <p className="text-gray-600">Loading assessment...</p>
            <p className="text-sm text-gray-400 mt-2">Question index: {currentQuestionIndex}, Total questions: {questions.length}</p>
          </div>
        </div>
      );
    }
    
    const completedQuestions = Object.keys(responses).length;
    const progress = (completedQuestions / questions.length) * 100;
    
    // Check which domains are completed
    const isDomainCompleted = (domainIndex) => {
      const domainQuestions = questions.filter(q => q.domain === domainIndex);
      return domainQuestions.every(q => responses[questions.indexOf(q)] !== undefined);
    };
    
    // Check if domain is currently active
    const isDomainActive = (domainIndex) => {
      return question.domain === domainIndex;
    };
    
    return (
      <div className={`max-w-4xl mx-auto p-4 space-y-4 ${assessmentStarting ? 'assessment-start-animation' : ''}`}>
        <style>{`
          @keyframes domainComplete {
            0% { transform: translateY(-50%) scale(1); }
            50% { transform: translateY(-50%) scale(1.2); }
            100% { transform: translateY(-50%) scale(1.05); }
          }
          .domain-completed {
            animation: domainComplete 0.6s ease-out;
          }
        `}</style>
        {/* Header with Logo */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <a 
              href="https://upskillingcircle.com" 
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="/upskilling logo - horizontal.png" 
                alt="Upskilling Circle - Where Human Potential Meets Intelligent Technology" 
                className="h-12 w-auto cursor-pointer"
              />
            </a>
            <div className="text-right min-w-[200px]">
              <h1 className="text-xl font-bold" style={{color: '#203659'}}>AI Readiness Assessment</h1>
              <p className="text-sm text-gray-600">Interactive Chat</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="relative mt-4 pt-4 pb-8 px-8">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${progress}%`,
                    background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)'
                  }}
                ></div>
              </div>
              
              {/* Domain nodes */}
              <div className="absolute top-4 left-8 right-8 h-2">
                {/* Sponsorship domain - starts at 0, ends at question 5 (25% of 20 questions) */}
                <div className="relative">
                  <div 
                    className={`absolute top-1/2 transform -translate-y-1/2 w-10 h-10 border-2 rounded-full flex items-center justify-center cursor-pointer transition-all duration-500 hover:scale-110 hover:shadow-lg group ${
                      isDomainCompleted(0) 
                        ? 'border-orange-500 domain-completed' 
                        : isDomainActive(0)
                        ? 'bg-white border-orange-500'
                        : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                    style={{ 
                      left: '0%',
                      background: isDomainCompleted(0) ? 'linear-gradient(45deg, #F58A1F, #FF6A1A)' : 'white'
                    }}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.parentElement.querySelector('.domain-tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.parentElement.querySelector('.domain-tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}
                  >
                    <FlagTriangleRight className={`w-5 h-5 transition-all duration-300 group-hover:scale-105 ${
                      isDomainCompleted(0) ? 'text-white' : isDomainActive(0) ? 'text-orange-500' : 'text-blue-600'
                    }`} />
                  </div>
                  <div 
                    className="domain-tooltip absolute bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap"
                    style={{ opacity: '0', visibility: 'hidden', left: '0%', transform: 'translateX(calc(-50% + 20px))', top: '-3rem' }}
                  >
                    Sponsorship - Leadership commitment and resource allocation
                    <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
                {isDomainActive(0) && (
                  <div className="absolute top-8 text-xs font-medium text-orange-600 whitespace-nowrap" style={{ left: '0%', transform: 'translateX(calc(-50% + 20px))' }}>
                    {domains[0].name}
                  </div>
                )}
                
                {/* Technology domain - starts at question 6 (25% of 20 questions) */}
                <div className="relative">
                  <div 
                    className={`absolute top-1/2 transform -translate-y-1/2 w-10 h-10 border-2 rounded-full flex items-center justify-center cursor-pointer transition-all duration-500 hover:scale-110 hover:shadow-lg group ${
                      isDomainCompleted(1) 
                        ? 'border-orange-500 domain-completed' 
                        : isDomainActive(1)
                        ? 'bg-white border-orange-500'
                        : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                    style={{ 
                      left: '25%',
                      background: isDomainCompleted(1) ? 'linear-gradient(45deg, #F58A1F, #FF6A1A)' : 'white'
                    }}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.parentElement.querySelector('.domain-tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.parentElement.querySelector('.domain-tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}
                  >
                    <Cpu className={`w-5 h-5 transition-all duration-300 group-hover:scale-105 ${
                      isDomainCompleted(1) ? 'text-white' : isDomainActive(1) ? 'text-orange-500' : 'text-blue-600'
                    }`} />
                  </div>
                  <div 
                    className="domain-tooltip absolute bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap"
                    style={{ opacity: '0', visibility: 'hidden', left: '25%', transform: 'translateX(calc(-50% + 20px))', top: '-3rem' }}
                  >
                    Technology - Data management and system integration
                    <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
                {isDomainActive(1) && (
                  <div className="absolute top-8 text-xs font-medium text-orange-600 whitespace-nowrap" style={{ left: '25%', transform: 'translateX(calc(-50% + 20px))' }}>
                    {domains[1].name}
                  </div>
                )}
                
                {/* People domain - starts at question 11 (50% of 20 questions) */}
                <div className="relative">
                  <div 
                    className={`absolute top-1/2 transform -translate-y-1/2 w-10 h-10 border-2 rounded-full flex items-center justify-center cursor-pointer transition-all duration-500 hover:scale-110 hover:shadow-lg group ${
                      isDomainCompleted(2) 
                        ? 'border-orange-500 domain-completed' 
                        : isDomainActive(2)
                        ? 'bg-white border-orange-500'
                        : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                    style={{ 
                      left: '50%',
                      background: isDomainCompleted(2) ? 'linear-gradient(45deg, #F58A1F, #FF6A1A)' : 'white'
                    }}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.parentElement.querySelector('.domain-tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.parentElement.querySelector('.domain-tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}
                  >
                    <Users className={`w-5 h-5 transition-all duration-300 group-hover:scale-105 ${
                      isDomainCompleted(2) ? 'text-white' : isDomainActive(2) ? 'text-orange-500' : 'text-blue-600'
                    }`} />
                  </div>
                  <div 
                    className="domain-tooltip absolute bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap"
                    style={{ opacity: '0', visibility: 'hidden', left: '50%', transform: 'translateX(calc(-50% + 20px))', top: '-3rem' }}
                  >
                    People - Team capacity and cultural readiness
                    <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
                {isDomainActive(2) && (
                  <div className="absolute top-8 text-xs font-medium text-orange-600 whitespace-nowrap" style={{ left: '50%', transform: 'translateX(calc(-50% + 20px))' }}>
                    {domains[2].name}
                  </div>
                )}
                
                {/* Processes domain - starts at question 17 (80% of 20 questions) */}
                <div className="relative">
                  <div 
                    className={`absolute top-1/2 transform -translate-y-1/2 w-10 h-10 border-2 rounded-full flex items-center justify-center cursor-pointer transition-all duration-500 hover:scale-110 hover:shadow-lg group ${
                      isDomainCompleted(3) 
                        ? 'border-orange-500 domain-completed' 
                        : isDomainActive(3)
                        ? 'bg-white border-orange-500'
                        : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                    style={{ 
                      left: '80%',
                      background: isDomainCompleted(3) ? 'linear-gradient(45deg, #F58A1F, #FF6A1A)' : 'white'
                    }}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.parentElement.querySelector('.domain-tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.parentElement.querySelector('.domain-tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}
                  >
                    <Split className={`w-5 h-5 transition-all duration-300 group-hover:scale-105 ${
                      isDomainCompleted(3) ? 'text-white' : isDomainActive(3) ? 'text-orange-500' : 'text-blue-600'
                    }`} style={{ transform: 'rotate(90deg)' }} />
                  </div>
                  <div 
                    className="domain-tooltip absolute bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap"
                    style={{ opacity: '0', visibility: 'hidden', left: '80%', transform: 'translateX(calc(-50% + 20px))', top: '-3rem' }}
                  >
                    Processes - Documentation and performance measurement
                    <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
                {isDomainActive(3) && (
                  <div className="absolute top-8 text-xs font-medium text-orange-600 whitespace-nowrap" style={{ left: '80%', transform: 'translateX(calc(-50% + 20px))' }}>
                    {domains[3].name}
                  </div>
                )}
                
                {/* Finish Line - at 100% */}
                <div className="relative">
                  <div 
                    className={`absolute top-1/2 transform -translate-y-1/2 w-10 h-10 border-2 rounded-full flex items-center justify-center cursor-pointer transition-all duration-500 hover:scale-110 hover:shadow-lg group ${
                      completedQuestions === questions.length
                        ? 'border-green-500 bg-green-500' 
                        : 'bg-white border-gray-300'
                    }`}
                    style={{ left: '100%', transform: 'translateX(-100%) translateY(-50%)' }}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.parentElement.querySelector('.domain-tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.parentElement.querySelector('.domain-tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}
                  >
                    <CheckCircle className={`w-5 h-5 transition-all duration-300 group-hover:scale-105 ${
                      completedQuestions === questions.length ? 'text-white' : 'text-gray-400'
                    }`} />
                  </div>
                  <div 
                    className="domain-tooltip absolute bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap"
                    style={{ opacity: '0', visibility: 'hidden', left: '100%', transform: 'translateX(calc(-50% - 20px))', top: '-3rem' }}
                  >
                    Assessment Complete
                    <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              Question {currentQuestionIndex + 1} of {questions.length}
            </h3>
          </div>
          
          <div className="space-y-3">
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{question.question}</p>
            
            {conversationHistory[currentQuestionIndex] && conversationHistory[currentQuestionIndex].length > 0 && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium text-gray-800 text-sm mb-2">Conversation:</h4>
                <div ref={conversationRef} className="max-h-80 overflow-y-auto space-y-2 pr-2" style={{scrollbarWidth: 'thin'}}>
                  {conversationHistory[currentQuestionIndex].map((msg, index) => (
                    <div key={index} className={`p-2 rounded ${
                      msg.role === 'user' 
                        ? 'bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 text-gray-800 ml-4' 
                        : 'bg-white text-gray-700 mr-4 border border-gray-200'
                    }`}>
                      <div className="text-xs font-medium mb-1 opacity-75">
                        {msg.role === 'user' ? 'You' : 'Assessment AI'}
                      </div>
                      <div className="text-sm whitespace-pre-line">{msg.content}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Connection Status and User Options */}
          {connectionState === 'retrying' && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-yellow-800 font-medium">
                  My AI brain is not responding... attempting reconnection ({currentRetryAttempt}/3)
                </span>
              </div>
            </div>
          )}

          {connectionState === 'user_choice' && showConnectionOptions && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-yellow-800 font-medium">
                    My AI brain is not responding... {retryCountdown > 0 ? `trying to reconnect in ${retryCountdown} seconds` : `attempting reconnection (${currentRetryAttempt}/3)`}
                  </span>
                </div>

                <div className="text-center sm:text-right">
                  <button
                    onClick={() => handleUserConnectionChoice('use_numbers', currentQuestionIndex, currentResponse)}
                    className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                  >
                    Show Choices Instead
                  </button>
                </div>
              </div>
            </div>
          )}

          {connectionState === 'failed' && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-red-800 font-medium">
                    Unable to connect to AI analysis after multiple attempts
                  </span>
                </div>

                <div className="text-center sm:text-right">
                  <button
                    onClick={() => handleUserConnectionChoice('use_numbers', currentQuestionIndex, currentResponse)}
                    className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                  >
                    Show Choices Instead
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleQuickAction("Clarify the question")}
                  disabled={isProcessing}
                  className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  Clarify the question
                </button>
                {!usedProvideChoices.has(currentQuestionIndex) && (
                  <button
                    type="button"
                    onClick={() => handleQuickAction("Provide Choices")}
                    disabled={isProcessing}
                    className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    Provide Choices
                  </button>
                )}
              </div>
              
              <div className="flex gap-3 items-end">
                <textarea
                  value={currentResponse}
                  onChange={(e) => setCurrentResponse(e.target.value)}
                  onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleResponseSubmit();
                  }
                }}

                  placeholder={waitingForClarification ? "Please provide more details..." : "Please share your thoughts in detail..."}
                  className="flex-1 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={4}
                />
                
                <button
                  type="button"
                  onClick={() => handleResponseSubmit()}
                  disabled={!currentResponse || typeof currentResponse !== 'string' || !currentResponse.trim() || isProcessing}
                  className="text-white p-4 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center transform hover:scale-105"
                  style={{
                    background: isProcessing ? '#9CA3AF' : 'linear-gradient(45deg, #F58A1F, #FF6A1A)',
                    boxShadow: isProcessing ? 'none' : '0 4px 15px rgba(245, 138, 31, 0.3)'
                  }}
                >
                  {isProcessing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setShowEndAssessmentModal(true)}
                className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                End Assessment
              </button>
            </div>
            
            {/* Skip/Report buttons - less prominent */}
            <div className="border-t border-gray-100 pt-3 mt-6">
              <p className="text-xs text-gray-400 text-center mb-2">Having difficulty with this question?</p>
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  onClick={() => skipQuestion('user_skip')}
                  disabled={isProcessing}
                  className="text-xs text-gray-500 hover:text-gray-700 underline transition-colors disabled:text-gray-300"
                  title="Skip this question and continue"
                >
                  Skip Question
                </button>
                <span className="text-xs text-gray-300">•</span>
                <button
                  type="button"
                  onClick={openReportModal}
                  disabled={isProcessing}
                  className="text-xs text-gray-500 hover:text-gray-700 underline transition-colors disabled:text-gray-300"
                  title="Report an issue with this question"
                >
                  Report Issue
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Report Issue Modal */}
        {showReportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Report Issue</h3>
              <p className="text-sm text-gray-600 mb-4">
                Please describe the issue you encountered with this question. Your feedback helps us improve the assessment.
              </p>
              <textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Describe the issue (e.g., question unclear, AI not responding, technical problem...)"
                className="w-full p-3 border border-gray-300 rounded-lg resize-none h-24 text-sm"
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={submitReport}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  Submit Report
                </button>
                <button
                  onClick={cancelReport}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* End Assessment Modal */}
        {showEndAssessmentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">End Assessment Early?</h3>
              <p className="text-sm text-gray-600 mb-6">
                Ending the assessment now will result in an <strong>incomplete assessment report</strong>. 
                Your results will be based only on the questions you've answered so far, which may not 
                provide a comprehensive evaluation of your AI readiness.
              </p>
              <p className="text-sm text-gray-600 mb-6">
                <strong>Recommendation:</strong> Continue the assessment to receive the most accurate and 
                valuable insights for your organization.
              </p>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    setCurrentStage('results');
                    setShowEndAssessmentModal(false);
                  }}
                  className="flex-1 border border-gray-300 text-gray-600 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  End Anyway
                </button>
                <button
                  onClick={() => setShowEndAssessmentModal(false)}
                  autoFocus
                  className="flex-1 text-white py-2 px-4 rounded-lg transition-all duration-300 text-sm font-medium transform hover:scale-105 shadow-lg"
                  style={{
                    background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)',
                    boxShadow: '0 4px 15px rgba(245, 138, 31, 0.3)'
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setShowEndAssessmentModal(false);
                    }
                  }}
                >
                  Continue Assessment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const ResultsComponent = () => {
    // Move useRef to component level
    const alreadySent = React.useRef(false);
    
    const domainScores = calculateDomainScores();
    const totalScore = domainScores.reduce((a, b) => a + b, 0);
    const completedQuestions = Object.keys(responses).length;
    const isComplete = completedQuestions === questions.length;
    
    const getReadinessLevel = (score) => {
      if (score <= 8) return { level: 'Early Stage', color: 'text-red-600', bg: 'bg-red-50' };
      if (score <= 12) return { level: 'Developing', color: 'text-yellow-600', bg: 'bg-yellow-50' };
      if (score <= 16) return { level: 'Advanced', color: 'text-blue-600', bg: 'bg-blue-50' };
      return { level: 'Leading', color: 'text-green-600', bg: 'bg-green-50' };
    };
    
    const readiness = getReadinessLevel(totalScore);

    // Restart assessment function with previous submission ID tracking
    const restartAssessment = async () => {
      // Step 1: Set restart flag to prevent useEffect triggers
      setIsRestarting(true);

      const previousSubmissionId = submissionId; // Store current submission ID

      // Generate new submission ID
      const now = new Date();
      const newSubmissionId = now.getFullYear().toString() +
                              (now.getMonth() + 1).toString().padStart(2, '0') +
                              now.getDate().toString().padStart(2, '0') +
                              now.getHours().toString().padStart(2, '0') +
                              now.getMinutes().toString().padStart(2, '0') +
                              now.getSeconds().toString().padStart(2, '0');

      // Update submission ID state
      setSubmissionId(newSubmissionId);

      // Send restart capture to Notion with audit trail
      const restartData = {
        submission_id: newSubmissionId,
        timestamp: new Date().toISOString(),
        name: userName,
        email: userEmail,
        phone: userPhone || '',
        organization: userOrganization || '',
        position: userPosition,
        oversight: oversightAreas,
        isQualified: isQualified,
        status: 'initial_capture',
        restart_indicator: true,
        previous_submission_id: previousSubmissionId,
        // Reset assessment fields
        answered_questions: 0,
        overall_score: 0,
        completion_percentage: 0,
        sponsorship_score: 0,
        technology_score: 0,
        people_score: 0,
        processes_score: 0,
        skipped_questions: 0,
        reported_issues: 0,
        total_time_minutes: 0,
        average_question_time: 0
      };

      try {

        const response = await fetch(NOTION_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(restartData)
        });

        if (!response.ok) {
          throw new Error(`Notion API failed: ${response.status}`);
        }

        const responseData = await response.json();
      } catch (error) {
        console.error('❌ Restart capture failed:', error);
        // Continue with restart even if Notion fails - user experience priority
      }

      // Reset all assessment state
      setResponses({});
      setScores({});
      setCurrentQuestionIndex(0);
      setConversationHistory([]);
      setAssessmentStartTime(null);
      setQuestionStartTimes({});
      setQuestionDurations({});
      setSkippedQuestions(new Set());
      setReportedIssues({});

      // Reset choice and scoring tracking
      setUsedProvideChoices(new Set());
      setScoringMethods({});
      setConfidenceLevels({});

      // Reset UI states
      setWaitingForClarification(false);
      setCurrentResponse('');
      setIsProcessing(false);

      // Reset connection retry states
      setConnectionState('connected');
      setCurrentRetryAttempt(0);
      setRetryCountdown(0);

      // Navigate to assessment stage
      setCurrentStage('assessment');

      // Reset send tracking
      alreadySent.current = false;

      // Start fresh assessment timer
      setAssessmentStartTime(Date.now());

      // Step 6: Clear restart flag after short delay
      setTimeout(() => {
        setIsRestarting(false);
      }, 100);
    };

    useEffect(() => {
      const prepareAndSendData = async () => {
        const timingReport = getTimingReport();
        
        // Data validation and cleanup
        const cleanedConversation = questions.map((q, index) => {
          const hasResponse = responses[index] && responses[index] !== null;
          const isSkipped = skippedQuestions.has(index);
          const cleanResponse = hasResponse && !responses[index].startsWith('SKIPPED_') ? responses[index] : null;
          
          return {
            questionNumber: index + 1,
            domain: domains[q.domain].name,
            question: q.question,
            response: responses[index] || null,
            cleanResponse: cleanResponse,
            score: scores[index] || 0,
            timeSpentSeconds: questionDurations[index] ? Math.round(questionDurations[index] / 1000) : 0,
            wasSkipped: isSkipped,
            status: isSkipped ? 'SKIPPED' : (hasResponse ? 'ANSWERED' : 'UNANSWERED'),
            reportedIssue: reportedIssues[index] || null,
            conversationLength: conversationHistory[index] ? conversationHistory[index].length : 0,
            fullConversation: conversationHistory[index] ? conversationHistory[index].map(msg => ({
               role: msg.role,
               content: msg.content
             })) : [],
            scoringMethod: scoringMethods[index] || 'ai' // Track whether AI-analyzed or self-scored
          };
        }).filter(item => item.response !== null);

        // Prepare confidence data
        const confidenceDataArray = [];
        const lowConfidenceScores = [];
        
        questions.forEach((question, index) => {
          if (scores[index] && confidenceLevels[index]) {
            confidenceDataArray.push({
              question: index + 1,
              score: scores[index],
              confidence: confidenceLevels[index],
              domain: domains[question.domain].name
            });
            
            if (confidenceLevels[index] === 'LOW') {
              lowConfidenceScores.push({
                question: index + 1,
                score: scores[index],
                domain: domains[question.domain].name,
                response: responses[index] || 'N/A'
              });
            }
          }
        });
        
        // Use existing submissionId from state or generate a new one
        let currentSubmissionId = submissionId;
        if (!currentSubmissionId) {
          const now = new Date();
          currentSubmissionId = now.getFullYear().toString() + 
                               (now.getMonth() + 1).toString().padStart(2, '0') +
                               now.getDate().toString().padStart(2, '0') +
                               now.getHours().toString().padStart(2, '0') +
                               now.getMinutes().toString().padStart(2, '0') +
                               now.getSeconds().toString().padStart(2, '0');
          setSubmissionId(currentSubmissionId);
        }
        
        const assessmentData = {
          submissionId: currentSubmissionId,
          timestamp: new Date().toISOString(),
          // Contact information
          name: userName.trim(),
          email: userEmail.trim(),
          phone: userPhone.trim(),
          organization: userOrganization.trim(),
          completionStatus: {
            isComplete: isComplete,
            answeredQuestions: completedQuestions,
            totalQuestions: questions.length,
            completionPercentage: Math.round((completedQuestions / questions.length) * 100)
          },
          timing: {
            totalTimeSeconds: timingReport.totalTimeSeconds,
            totalTimeText: timingReport.totalTimeText,
            averageQuestionTimeSeconds: timingReport.averageQuestionTime,
            questionTimings: timingReport.questionTimes
          },
          skipAndIssueData: {
            skippedQuestions: Array.from(skippedQuestions),
            totalSkipped: skippedQuestions.size,
            reportedIssues: Object.keys(reportedIssues).length > 0 ? reportedIssues : null,
            totalReported: Object.keys(reportedIssues).length
          },
          scores: {
            overall: totalScore,
            readinessLevel: readiness.level,
            domains: {
              sponsorship: domainScores[0],
              technology: domainScores[1],
              people: domainScores[2],
              processes: domainScores[3]
            }
          },
          confidenceData: confidenceDataArray,
          lowConfidenceScores: lowConfidenceScores,
          conversation: cleanedConversation,
          detailedResponses: responses,
          detailedScores: scores,
          detailedConfidenceLevels: confidenceLevels
        };
        
        // Send to Notion API with appropriate status
        const status = isComplete ? 'completed' : 'partial_completion';
        await sendToNotion(assessmentData, status);
        
        // Send results email only for completed assessments
        if (status === 'completed') {
          console.log('📧 Assessment completed - sending results email');
          try {
            const emailResult = await sendResultsEmail(assessmentData);
            if (emailResult.success) {
              console.log('✅ Results email sent successfully');
            } else {
              console.error('❌ Results email failed:', emailResult.error);
            }
          } catch (emailError) {
            console.error('❌ Email sending error:', emailError);
            // Don't block the user experience if email fails
          }
        }
      };
      
      // Prevent data submission during restart
      if (isRestarting) return;

      if (completedQuestions > 0 && !alreadySent.current) {
        alreadySent.current = true;
        prepareAndSendData();
      }
    }, [completedQuestions, isRestarting]); // Add isRestarting dependency
    
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Header with Logo */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <a 
              href="https://upskillingcircle.com" 
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="/upskilling logo - horizontal.png" 
                alt="Upskilling Circle - Where Human Potential Meets Intelligent Technology" 
                className="h-12 w-auto cursor-pointer"
              />
            </a>
            <div className="text-center sm:text-right">
              <h2 className="text-xl font-bold" style={{ color: '#203659' }}>AI Readiness Assessment</h2>
              <p className="text-gray-600 text-sm">
                {userName ? `${userName}'s Personalized Results` : 'Your Personalized Results'}
              </p>
            </div>
          </div>
        </div>

        <div className="text-center space-y-4">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
          <h1 className="text-3xl font-bold text-gray-800">Assessment Complete!</h1>
          {!isComplete && (
            <div className="text-center space-y-2">
              <p className="text-yellow-600 font-medium">
                Note: This report is based on {completedQuestions} of {questions.length} questions
              </p>
              <p className="text-gray-600 text-sm">
                For the most accurate results, consider <button
                  onClick={restartAssessment}
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  taking the full assessment
                </button>
              </p>
            </div>
          )}
        </div>

        <div className={`p-6 rounded-lg ${readiness.bg}`}>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Executive Summary</h2>
          <p className="text-gray-700 mb-4">
            This assessment evaluates your organization's readiness across four critical domains for AI and automation 
            implementation in operations: Sponsorship, Technology, People, and Processes.
          </p>
          <div className="flex items-center gap-2">
            <span className="text-gray-700 font-medium">Overall Readiness Classification:</span>
            <span className={`font-bold text-xl ${readiness.color}`}>{readiness.level}</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-xl font-bold text-gray-800 mb-6">Results Overview</h3>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              {domains.map((domain, index) => {
                const Icon = domain.icon;
                const score = domainScores[index];
                const width = (score / 5) * 100;
                
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="w-5 h-5 text-gray-600" />
                        <span className="font-medium text-gray-800">{domain.name}</span>
                      </div>
                      <span className="font-bold text-gray-800">{score}/5</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="h-3 rounded-full transition-all duration-500"
                        style={{
                          width: `${width}%`,
                          background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)'
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex items-center justify-center">
              <div className="w-full max-w-md">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold mb-2" style={{ color: '#203659' }}>{totalScore}/20</div>
                  <div className="text-gray-600">Overall Score</div>
                  <div className={`font-semibold mt-2 ${readiness.color}`}>{readiness.level}</div>
                </div>
                
                {/* Visual Scale */}
                <div className="relative">
                  {/* Scale background with color segments */}
                  <div className="relative h-8 rounded-full overflow-hidden border-2 border-gray-300 bg-white">
                    <div className="absolute inset-0 flex">
                      {/* Early Stage: 1-8 (40% of scale) */}
                      <div className="w-[40%] bg-red-200"></div>
                      {/* Developing: 9-12 (20% of scale) */}
                      <div className="w-[20%] bg-yellow-200"></div>
                      {/* Advanced: 13-16 (20% of scale) */}
                      <div className="w-[20%] bg-blue-200"></div>
                      {/* Leading: 17-20 (20% of scale) */}
                      <div className="w-[20%] bg-green-200"></div>
                    </div>
                    
                    {/* Score indicator */}
                    <div 
                      className="absolute top-0 bottom-0 w-1 bg-gray-800 transform -translate-x-0.5 z-10"
                      style={{ left: `${(totalScore / 20) * 100}%` }}
                    >
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                        <div className="bg-gray-800 text-white px-2 py-1 rounded text-xs font-bold">
                          {totalScore}
                        </div>
                        <div className="w-2 h-2 bg-gray-800 transform rotate-45 mx-auto -mt-1"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Scale labels */}
                  <div className="flex justify-between mt-3 text-xs font-medium">
                    <div className="text-red-600 w-[40%] text-left">
                      <div>Early Stage</div>
                      <div className="text-gray-500">1-8</div>
                    </div>
                    <div className="text-yellow-600 w-[20%] text-center">
                      <div>Developing</div>
                      <div className="text-gray-500">9-12</div>
                    </div>
                    <div className="text-blue-600 w-[20%] text-center">
                      <div>Advanced</div>
                      <div className="text-gray-500">13-16</div>
                    </div>
                    <div className="text-green-600 w-[20%] text-right">
                      <div>Leading</div>
                      <div className="text-gray-500">17-20</div>
                    </div>
                  </div>
                  
                  {/* Scale numbers */}
                  <div className="flex justify-between mt-2 text-xs text-gray-400">
                    <span>1</span>
                    <span>5</span>
                    <span>10</span>
                    <span>15</span>
                    <span>20</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Summary */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          {(() => {
            const aiCount = Object.values(scoringMethods).filter(method => method === 'ai').length;
            const selfCount = Object.values(scoringMethods).filter(method => method === 'self').length;
            const totalResponses = Object.keys(responses).length;

            if (aiCount === totalResponses) {
              return (
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">Analysis Summary</h4>
                    <p className="text-sm text-gray-600">
                      {totalResponses}/{totalResponses} of your responses were scored by our AI. This usually gives very good results, and our team might review your answers to make sure your personalized report is right on point.
                    </p>
                  </div>
                </div>
              );
            } else {
              return (
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">Analysis Summary</h4>
                    <p className="text-sm text-gray-600">
                      {aiCount} responses were scored by AI, while {selfCount} responses were self-scored using numbered options. Our team will review your submission and take this into consideration in generating your personalized report.
                    </p>
                  </div>
                </div>
              );
            }
          })()}
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Next Steps</h3>
          <p className="text-gray-700 mb-6">
            Your comprehensive report is almost ready! To finalize it, we invite you to a brief interview 
            to validate and deepen what we've prepared based on your assessment. After our conversation, you'll receive your 
            personalized report with comprehensive executive summary, detailed domain analysis, prioritized focus areas, 
            curated resources, and a personalized action plan tailored to your organization's unique context and needs.
          </p>
          
          <div className="text-center">
            <a
              href={`https://calendly.com/upskilling-circle/ai_assessment_debrief_call?name=${encodeURIComponent(userName)}&email=${encodeURIComponent(userEmail)}&a1=${submissionId || 'unknown'}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white px-8 py-3 rounded-lg transition-all duration-300 inline-flex items-center gap-2 transform hover:scale-105 shadow-lg"
              style={{
                background: 'linear-gradient(45deg, #F58A1F, #FF6A1A)',
                boxShadow: '0 4px 15px rgba(245, 138, 31, 0.3)'
              }}
            >
              Schedule Your Interview
              <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    );
  };

  // Add this new helper function
  const summarizeContext = async (conversation) => {
    if (conversation.length <= 1) return '';
    
    // Skip the most recent exchange since it will be included as latest response
    const previousConversation = conversation.slice(0, -1);
    
    // Extract key points from previous exchanges
    const contextPoints = previousConversation.reduce((acc, msg) => {
      if (msg.role === 'user') {
        acc.push(msg.content);
      }
      return acc;
    }, []);

    if (contextPoints.length === 0) return '';

    return `\nCONTEXT FROM PREVIOUS EXCHANGES:\n- ${contextPoints.join('\n- ')}\n`;
  };

  return (
    <div className="min-h-screen" style={{ background: currentStage === 'welcome' ? 'transparent' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
      {currentStage === 'welcome' && renderWelcome()}
      {currentStage === 'qualification' && renderQualification()}
      {currentStage === 'disqualified' && renderDisqualified()}
      {currentStage === 'assessment' && renderAssessment()}
      {currentStage === 'results' && <ResultsComponent />}
      {showUserInfoModal && renderUserInfoModal()}
    </div>
  );
};

export default AIReadinessAssessment;